import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { HubPost } from "@/hooks/hub/useHubFeed";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB pinned post for a SINGLE feed view.
 *
 * One pin per feed is enforced by a partial unique index on hub_posts
 * (feed_id) WHERE pinned_at IS NOT NULL, and set only via the admin-only
 * `hub_admin_pin_post` RPC. Home (feedId = null) does NOT float pins — the
 * pinned marker just renders in chronological position there.
 *
 * ISOLATION: hub_posts / hub_feeds only.
 */
export function useHubPinnedPost(feedId: string | null) {
  const { blockedIds } = useBlockedUserIds();
  const query = useQuery({
    queryKey: ["hub-pinned-post", feedId ?? "home", blockedIds.join(",")],
    enabled: !!feedId,
    staleTime: 30_000,
    queryFn: async (): Promise<HubPost | null> => {
      let q = supabase
        .from("hub_posts")
        .select("*, feed:hub_feeds(slug, name)")
        .eq("feed_id", feedId as string)
        .eq("is_deleted", false)
        .eq("moderation_status", "approved")
        .not("pinned_at", "is", null)
        .order("pinned_at", { ascending: false })
        .limit(1);
      if (blockedIds.length) q = q.not("author_id", "in", `(${blockedIds.join(",")})`);
      const { data: rows, error } = await q;
      if (error) throw error;
      const data = (rows ?? [])[0] ?? null;
      return (data as HubPost | null) ?? null;
    },
  });

  return {
    pinned: feedId ? query.data ?? null : null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export default useHubPinnedPost;
